import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, onSnapshot, orderBy, query, where, type Timestamp } from 'firebase/firestore';
import { FileText, Phone, Mail, AlertTriangle } from 'lucide-react';
import { db } from '../../lib/firebase';
import { FilterChip } from '../../components/FilterChip';

// Owner view of every discovery-flight guest: the person (from the waiver they
// signed on the CFI's phone), the flight it was for, and the signed PDF. Also
// lists discovery bookings that have NO waiver yet, since the waiver is the gate
// for logging that flight. Read-only: waivers are write-once by rule.

interface Waiver {
  id: string;
  version?: string;
  bookingId?: string | null;
  cfiUid: string;
  cfiName?: string;
  guest?: {
    name?: string;
    dob?: string;
    email?: string;
    phone?: string;
    weight?: string;
    emergency?: string;
    usCitizen?: boolean;
  };
  minor?: boolean;
  parent?: { name?: string; relationship?: string; phone?: string; email?: string } | null;
  signaturePng?: string;
  parentSignaturePng?: string;
  clientSignedAt?: string;
  createdAt?: Timestamp;
  signedAt?: Timestamp;
  pdfUrl?: string;
  status?: string;
  emailedAt?: Timestamp;
  emailError?: string;
}

interface DiscoveryBooking {
  id: string;
  lesseeName?: string;
  lesseeUid: string;
  discoveryGuest?: string;
  waiverId?: string;
  status: string;
  startDateTime?: Timestamp;
  guestCardLast4?: string;
  guestCardBrand?: string;
}

interface DiscoveryFlight {
  id: string;
  bookingId?: string;
  date: string;
  hoursFlown: number;
  lesseeUid: string;
  // Fuel-included discovery (10/26/26): what the guest's card was charged when the CFI logged it.
  guestCharge?: { status: 'paid' | 'failed' | 'no_takeoff'; amount: number; rate: number; card?: string; failureReason?: string };
}

const fmtWhen = (t?: Timestamp | null) =>
  t ? t.toDate().toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

function ageFrom(dob?: string): string {
  if (!dob) return '';
  const d = new Date(`${dob}T12:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return `${age}`;
}

const telHref = (p?: string) => (p ? `tel:${p.replace(/[^\d+]/g, '')}` : undefined);

export function DiscoveryGuests() {
  const [waivers, setWaivers] = useState<Waiver[] | null>(null);
  const [bookings, setBookings] = useState<DiscoveryBooking[]>([]);
  const [flights, setFlights] = useState<DiscoveryFlight[]>([]);
  const [showTests, setShowTests] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!db) return;
    const u1 = onSnapshot(
      query(collection(db, 'discoveryWaivers'), orderBy('createdAt', 'desc')),
      (snap) => setWaivers(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Waiver, 'id'>) }))),
      () => setErr('Could not load waivers.')
    );
    const u2 = onSnapshot(
      query(collection(db, 'bookings'), where('flightPurpose', '==', 'discovery')),
      (snap) => setBookings(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<DiscoveryBooking, 'id'>) }))),
      () => {}
    );
    const u3 = onSnapshot(
      query(collection(db, 'flights'), where('billingMode', '==', 'discovery')),
      (snap) => setFlights(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<DiscoveryFlight, 'id'>) }))),
      () => {}
    );
    return () => {
      u1();
      u2();
      u3();
    };
  }, []);

  const bookingById = useMemo(() => new Map(bookings.map((b) => [b.id, b])), [bookings]);
  const flightsByBooking = useMemo(() => {
    const m = new Map<string, DiscoveryFlight[]>();
    for (const f of flights) if (f.bookingId) m.set(f.bookingId, [...(m.get(f.bookingId) ?? []), f]);
    return m;
  }, [flights]);

  // A waiver with no booking is the owner's test run of the flow.
  const isTest = (w: Waiver) => !w.bookingId;
  const shown = (waivers ?? []).filter((w) => showTests || !isTest(w));
  const testCount = (waivers ?? []).filter(isTest).length;

  // Discovery bookings still waiting on a waiver (upcoming or already past).
  const unsigned = bookings
    .filter((b) => !b.waiverId && b.status !== 'cancelled' && b.status !== 'denied')
    .sort((a, b) => (b.startDateTime?.toMillis?.() ?? 0) - (a.startDateTime?.toMillis?.() ?? 0));

  return (
    <div>
      {err && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{err}</div>}

      {unsigned.length > 0 && (
        <div className="card mb-4 p-5 border border-amber-200">
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className="mt-0.5 text-amber-600 shrink-0" />
            <div className="min-w-0">
              <h2 className="font-display text-lg font-semibold text-navy-900">No waiver on file</h2>
              <p className="mt-1 text-sm text-ink-muted">Discovery bookings whose guest hasn’t signed. The flight can’t be logged against the booking until they do.</p>
              <ul className="mt-3 space-y-1.5 text-sm">
                {unsigned.map((b) => {
                  const past = (b.startDateTime?.toMillis?.() ?? 0) < Date.now();
                  return (
                    <li key={b.id} className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                      <span className="font-medium text-navy-900">{b.discoveryGuest || 'Guest not named'}</span>
                      <span className="text-ink-muted">with {b.lesseeName || 'CFI'}</span>
                      <span className="text-ink-muted">{fmtWhen(b.startDateTime)}</span>
                      {past && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">slot has passed</span>}
                      <Link to="/admin/bookings" className="text-xs underline text-navy-700">Open booking</Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <FilterChip active={!showTests} onClick={() => setShowTests(false)}>Guests</FilterChip>
        <FilterChip active={showTests} onClick={() => setShowTests(true)}>Include owner tests{testCount ? ` (${testCount})` : ''}</FilterChip>
        <span className="ml-auto text-xs text-ink-muted">{waivers ? `${shown.length} signed` : ''}</span>
      </div>

      {waivers === null ? (
        <div className="card p-8 text-center text-ink-muted">Loading…</div>
      ) : shown.length === 0 ? (
        <div className="card p-8 text-center text-ink-muted">No signed waivers yet. They appear here the moment a guest signs on the CFI’s phone.</div>
      ) : (
        <div className="space-y-3">
          {shown.map((w) => {
            const g = w.guest ?? {};
            const b = w.bookingId ? bookingById.get(w.bookingId) : undefined;
            const flown = w.bookingId ? flightsByBooking.get(w.bookingId) ?? [] : [];
            const age = ageFrom(g.dob);
            const signed = w.signedAt ?? w.createdAt;
            return (
              <div key={w.id} className="card p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-lg font-semibold text-navy-900">
                      {g.name || 'Guest'}
                      {age && <span className="ml-2 text-sm font-normal text-ink-muted">{age}</span>}
                      {w.minor && <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">minor</span>}
                      {isTest(w) && <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">owner test</span>}
                    </div>
                    <div className="mt-1 text-sm text-ink-muted">
                      {[
                        `with ${w.cfiName || 'CFI'}`,
                        b?.startDateTime ? fmtWhen(b.startDateTime) : null,
                        flown.length ? `flown · ${flown.map((f) => `${f.hoursFlown.toFixed(1)} hr`).join(', ')}` : b ? 'not flown yet' : null,
                        b?.guestCardLast4 ? `card ${b.guestCardBrand ? `${b.guestCardBrand.toUpperCase()} ` : ''}•${b.guestCardLast4}` : b?.waiverId ? 'no card yet' : null,
                        ...flown.map((f) =>
                          f.guestCharge?.status === 'no_takeoff'
                            ? 'no takeoff, not charged'
                            : f.guestCharge?.status === 'paid'
                            ? `charged $${f.guestCharge.amount.toFixed(2)}`
                            : f.guestCharge?.status === 'failed'
                            ? `CHARGE FAILED $${f.guestCharge.amount.toFixed(2)}: ${f.guestCharge.failureReason ?? ''}`
                            : null
                        ),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {w.pdfUrl ? (
                      <a href={w.pdfUrl} target="_blank" rel="noreferrer" className="btn-secondary inline-flex items-center gap-1.5">
                        <FileText size={15} /> Signed waiver (PDF)
                      </a>
                    ) : (
                      <span className="text-xs text-ink-muted self-center">PDF still generating…</span>
                    )}
                  </div>
                </div>

                <dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                  <div>
                    <dt className="inline text-ink-muted">Phone: </dt>
                    <dd className="inline font-medium">
                      {g.phone ? (
                        <a href={telHref(g.phone)} className="inline-flex items-center gap-1"><Phone size={13} />{g.phone}</a>
                      ) : '—'}
                    </dd>
                  </div>
                  <div>
                    <dt className="inline text-ink-muted">Email: </dt>
                    <dd className="inline font-medium">
                      {g.email ? (
                        <a href={`mailto:${g.email}`} className="inline-flex items-center gap-1"><Mail size={13} />{g.email}</a>
                      ) : '—'}
                    </dd>
                  </div>
                  <div><dt className="inline text-ink-muted">Date of birth: </dt><dd className="inline font-medium">{g.dob || '—'}</dd></div>
                  <div><dt className="inline text-ink-muted">Weight: </dt><dd className="inline font-medium">{g.weight ? `${g.weight} lb` : '—'}</dd></div>
                  <div className="sm:col-span-2"><dt className="inline text-ink-muted">Emergency contact: </dt><dd className="inline font-medium">{g.emergency || '—'}</dd></div>
                  <div>
                    <dt className="inline text-ink-muted">U.S. citizen or national: </dt>
                    <dd className="inline font-medium">{g.usCitizen === true ? 'Yes' : g.usCitizen === false ? 'No — TSA approval needed before any lesson toward a certificate' : '—'}</dd>
                  </div>
                  <div><dt className="inline text-ink-muted">Signed: </dt><dd className="inline font-medium">{signed ? fmtWhen(signed) : w.clientSignedAt || '—'}</dd></div>
                  {w.minor && w.parent && (
                    <div className="sm:col-span-2">
                      <dt className="inline text-ink-muted">Parent / guardian: </dt>
                      <dd className="inline font-medium">
                        {[w.parent.name, w.parent.relationship, w.parent.phone, w.parent.email].filter(Boolean).join(' · ')}
                      </dd>
                    </div>
                  )}
                  {w.emailError && (
                    <div className="sm:col-span-2 text-amber-800"><dt className="inline">Guest copy not emailed: </dt><dd className="inline">{w.emailError}</dd></div>
                  )}
                </dl>

                {(w.signaturePng || w.parentSignaturePng) && (
                  <div className="mt-4 flex flex-wrap gap-6">
                    {w.signaturePng && (
                      <div>
                        <div className="text-[11px] uppercase tracking-wider text-ink-muted mb-1">Guest signature</div>
                        <img src={w.signaturePng} alt="" className="h-14 w-auto rounded border border-slate-200 bg-white px-2" />
                      </div>
                    )}
                    {w.parentSignaturePng && (
                      <div>
                        <div className="text-[11px] uppercase tracking-wider text-ink-muted mb-1">Parent signature</div>
                        <img src={w.parentSignaturePng} alt="" className="h-14 w-auto rounded border border-slate-200 bg-white px-2" />
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
